// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';

import { addTasksToRunAsync, getRunDetailAsync } from 'actions/labeling-runs-actions';
import { CombinedState } from 'reducers';

const NO_PROJECT_VALUE = -1;

async function fetchAllProjects(): Promise<any[]> {
    const all: any[] = [];
    let url = '/api/projects?page_size=100&org=';
    while (url) {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(url, { credentials: 'same-origin' });
        const data = await res.json();
        all.push(...(data.results || []));
        url = data.next || null;
    }
    return all;
}

interface TaskSlot {
    projectId: number | null;
    taskId: number | null;
}

interface Props {
    runId: number;
    visible: boolean;
    onClose: () => void;
}

async function fetchTasks(projectId: number): Promise<any[]> {
    let url: string;
    if (projectId === NO_PROJECT_VALUE) {
        url = '/api/tasks?page_size=500&org=';
    } else {
        const filter = JSON.stringify({ and: [{ '==': [{ var: 'project_id' }, projectId] }] });
        url = `/api/tasks?filter=${encodeURIComponent(filter)}&page_size=500&org=`;
    }
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/vnd.cvat+json; version=2.0' },
    });
    const data = await response.json();
    const results: any[] = data.results || [];
    if (projectId === NO_PROJECT_VALUE) {
        return results.filter((t: any) => t.project_id === null || t.project_id === undefined);
    }
    return results;
}

export default function AddTasksModal({ runId, visible, onClose }: Props): JSX.Element {
    const dispatch = useDispatch();
    const existingTasks = useSelector((state: CombinedState) => state.activeRun.tasks);
    const [slots, setSlots] = useState<TaskSlot[]>([{ projectId: null, taskId: null }]);
    const [projects, setProjects] = useState<any[]>([]);
    const [tasksByProject, setTasksByProject] = useState<Record<number, any[]>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (visible) {
            fetchAllProjects().then((result: any[]) => {
                setProjects(result);
            });
        }
    }, [visible]);

    const loadTasksForProject = async (projectId: number): Promise<void> => {
        const tasks = await fetchTasks(projectId);
        setTasksByProject((prev) => ({ ...prev, [projectId]: tasks }));
    };

    const handleProjectChange = async (index: number, projectId: number): Promise<void> => {
        const newSlots = [...slots];
        newSlots[index] = { projectId, taskId: null };
        setSlots(newSlots);
        await loadTasksForProject(projectId);
    };

    const handleTaskChange = (index: number, taskId: number): void => {
        const newSlots = [...slots];
        newSlots[index] = { ...newSlots[index], taskId };
        if (index === newSlots.length - 1) {
            newSlots.push({ projectId: newSlots[index].projectId, taskId: null });
        }
        setSlots(newSlots);
    };

    const handleRemoveSlot = (index: number): void => {
        if (slots.length === 1) {
            setSlots([{ projectId: null, taskId: null }]);
        } else {
            setSlots(slots.filter((_, i) => i !== index));
        }
    };

    const getSelectedTaskIds = (excludeIndex: number): Set<number> => {
        const ids = new Set<number>(existingTasks.map((t: any) => t.task_id));
        slots.forEach((slot, i) => {
            if (i !== excludeIndex && slot.taskId !== null) {
                ids.add(slot.taskId);
            }
        });
        return ids;
    };

    const handleOk = async (): Promise<void> => {
        const taskIds = slots.map((s) => s.taskId).filter((id): id is number => id !== null);
        if (!taskIds.length) { onClose(); return; }
        setLoading(true);
        try {
            await dispatch(addTasksToRunAsync(runId, taskIds) as any);
            await dispatch(getRunDetailAsync(runId) as any);
            setSlots([{ projectId: null, taskId: null }]);
            setTasksByProject({});
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const projectOptions = [
        { value: NO_PROJECT_VALUE, label: '(No project)' },
        ...projects.map((p: any) => ({ value: p.id, label: p.name })),
    ];

    return (
        <Modal
            title='Add Tasks to Run'
            visible={visible}
            onOk={handleOk}
            onCancel={() => { setSlots([{ projectId: null, taskId: null }]); setTasksByProject({}); onClose(); }}
            okText='Add'
            confirmLoading={loading}
            width={600}
        >
            {slots.map((slot, index) => {
                const usedTaskIds = getSelectedTaskIds(index);
                const availableTasks = slot.projectId !== null
                    ? (tasksByProject[slot.projectId] || []).filter((t: any) => !usedTaskIds.has(t.id))
                    : [];
                return (
                    <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <Select
                            showSearch
                            placeholder='Select project'
                            style={{ flex: 1 }}
                            value={slot.projectId ?? undefined}
                            filterOption={(input, option) =>
                                (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={projectOptions}
                            onChange={(val: number) => handleProjectChange(index, val)}
                        />
                        <Select
                            showSearch
                            placeholder='Select task'
                            style={{ flex: 1 }}
                            value={slot.taskId ?? undefined}
                            disabled={slot.projectId === null}
                            filterOption={(input, option) =>
                                (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={availableTasks.map((t: any) => ({ value: t.id, label: `Task #${t.id}` }))}
                            onChange={(val: number) => handleTaskChange(index, val)}
                        />
                        <Button
                            type='text'
                            icon={<CloseOutlined />}
                            onClick={() => handleRemoveSlot(index)}
                        />
                    </div>
                );
            })}
            <Button
                type='dashed'
                icon={<PlusOutlined />}
                style={{ width: '100%', marginTop: 4 }}
                onClick={() => setSlots((prev) => [...prev, { projectId: null, taskId: null }])}
            >
                Add another task
            </Button>
        </Modal>
    );
}
